import {
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';

import {
  AbstractControl,
  FormControl,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';

import { GeneralService } from 'src/app/services/general.service';
import { VerificacionPacienteService } from 'src/app/services/verificacion-paciente.service';

@Component({
  selector: 'app-validador-rut',
  templateUrl: './validador-rut.component.html',
  styleUrls: ['./validador-rut.component.css']
})
export class ValidadorRutComponent implements OnInit, OnDestroy {

  @Output() resultadoValidacion = new EventEmitter<boolean>();
  @Output() rutValidado = new EventEmitter<string>();

  public rutPacienteFind: FormControl;
  public submitted = false;
  public cargando = false;
  public estadoValidacion: 'idle' | 'success' | 'error' = 'idle';
  public mensajeValidacion = '';
  public rutConsultado = '';

  private resetTimeout: any = null;
  private readonly RESULT_VISIBLE_MS = 10000;

  constructor(
    public generalService: GeneralService,
    public verificacionPacienteService: VerificacionPacienteService
  ) {
    this.rutPacienteFind = new FormControl('', [
      Validators.required,
      this.rutChilenoValidator()
    ]);
  }

  ngOnInit(): void {}

  ngOnDestroy(): void {
    this.clearResetTimeout();
  }

  // =========================================================
  // VALIDACIÓN RUT
  // =========================================================

  private rutChilenoValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = String(control.value || '').trim();

      if (!value) return null;

      const isValid = this.generalService.validaRutChileno(value);
      return isValid ? null : { rutInvalido: true };
    };
  }

  // =========================================================
  // INPUT
  // =========================================================

  public onRutInput(): void {
    this.resetResultOnly();

    const rawValue = String(this.rutPacienteFind.value || '');
    const cleaned = rawValue
      .replace(/[^0-9kK]/g, '')
      .toUpperCase()
      .slice(0, 9);

    if (cleaned.length < 2) {
      this.rutPacienteFind.setValue(cleaned, { emitEvent: false });
      this.rutPacienteFind.updateValueAndValidity({ emitEvent: false });
      return;
    }

    const body = cleaned.slice(0, -1);
    const dv = cleaned.slice(-1);
    const formattedRut = `${this.formatRutBody(body)}-${dv}`;

    this.rutPacienteFind.setValue(formattedRut, { emitEvent: false });
    this.rutPacienteFind.updateValueAndValidity({ emitEvent: false });
  }

  private formatRutBody(body: string): string {
    if (!body) return '';
    return body.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  }

  // =========================================================
  // VERIFICAR PACIENTE
  // =========================================================

  public async verificarPaciente(): Promise<void> {
    this.submitted = true;
    this.rutPacienteFind.markAsTouched();
    this.rutPacienteFind.updateValueAndValidity();

    if (this.rutPacienteFind.invalid || this.cargando) return;

    const rut = String(this.rutPacienteFind.value || '').trim();
    if (!rut) return;

    this.clearResetTimeout();
    this.estadoValidacion = 'idle';
    this.mensajeValidacion = '';
    this.rutConsultado = rut;
    this.cargando = true;

    try {
      const response: any =
        await this.verificacionPacienteService.getVerificarPaciente({ rut });

      const hasActivePlan = response?.datos?.status === true;

      if (hasActivePlan) {
        this.estadoValidacion = 'success';
        this.mensajeValidacion =
          'Tiene un convenio activo en la plataforma de telemedicina.';
      } else {
        this.estadoValidacion = 'error';
        this.mensajeValidacion =
          'No encontramos un convenio activo para este RUT en la plataforma de telemedicina.';
      }

      this.resultadoValidacion.emit(hasActivePlan);
      this.rutValidado.emit(rut);
      this.scheduleReset();

    } catch (error) {
      console.error(
        '[ValidadorRutComponent] Error verificando paciente:',
        error
      );

      this.estadoValidacion = 'error';
      this.mensajeValidacion =
        'No fue posible verificar al paciente. Intenta nuevamente.';

      this.scheduleReset();

    } finally {
      this.cargando = false;
    }
  }

  // =========================================================
  // ENTER
  // =========================================================

  public onRutKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Enter') return;

    event.preventDefault();
    this.verificarPaciente();
  }

  // =========================================================
  // ERRORES
  // =========================================================

  public shouldShowRutError(): boolean {
    return !!(
      this.rutPacienteFind.invalid &&
      (this.submitted || this.rutPacienteFind.touched)
    );
  }

  // =========================================================
  // RESET
  // =========================================================

  public resetValidator(): void {
    this.clearResetTimeout();
    this.submitted = false;
    this.cargando = false;
    this.estadoValidacion = 'idle';
    this.mensajeValidacion = '';
    this.rutConsultado = '';
    this.rutPacienteFind.reset('');
  }

  private resetResultOnly(): void {
    this.clearResetTimeout();
    this.estadoValidacion = 'idle';
    this.mensajeValidacion = '';
    this.rutConsultado = '';
  }

  private scheduleReset(): void {
    this.clearResetTimeout();

    this.resetTimeout = setTimeout(
      () => this.resetValidator(),
      this.RESULT_VISIBLE_MS
    );
  }

  private clearResetTimeout(): void {
    if (!this.resetTimeout) return;

    clearTimeout(this.resetTimeout);
    this.resetTimeout = null;
  }
}